import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import findKey from 'lodash/findKey';
import styled from 'styled-components';
import { BreadcrumbSection, BreadcrumbDivider, Breadcrumb } from 'semantic-ui-react';
import HasRight from 'nallian-shared-ui/lib/layout/rights/HasRight';
import { hasRight } from 'nallian-shared-ui/lib/lib/routesHelper';
import { GetRights } from 'nallian-shared-ui/lib/selectors';
import { PageTitle, PageContent, Tab, Menu, Loader, Link } from 'nallian-shared-ui/lib/components';
import { useNavigate } from 'react-router-dom';
import appService from '../../services/appService';
import { TABS, ACTIONS } from '../../constants';
import AppInfo from './AppInfo';
import EditApp from '../EditApp';
import AppInstallations from '../appInstallations/AppInstallations';
import AppInstallationDetail from '../appInstallations/AppInstallationDetail';
import AppOrganisations from '../organisations/AppOrganisations';
import AppCommunities from '../communities/AppCommunities';
import appInstallationService from '../../services/appInstallationService';

const UPDATE_APP_RIGHT = 'apps/update';

const StyledBreadcrumb = styled(Breadcrumb)`
  &&& {
    font-size: 0.78571429em !important;
  }
`;

const TAB_INDEX_MAPPING = {
  [TABS.DETAILS]: 0,
  [TABS.INSTALLATIONS]: 1,
  [TABS.COMMUNITIES]: 2,
  [TABS.ORGANISATIONS]: 3,
};

const AppDetail = ({
  appFetch,
  dispatchAppGet,
  dispatchAppInstallationGet,
  appInstallationFetch,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tab, id, installationId } = useParams();
  const rights = useSelector(GetRights);

  useEffect(() => {
    if (id) dispatchAppGet(id, undefined, t);
  }, [id, t, dispatchAppGet]);

  const appName = useMemo(
    () => (appFetch.value?.id === id && !appFetch.pending ? appFetch.value.name : '...'),
    [appFetch, id]
  );

  useEffect(() => {
    if (id && installationId && installationId !== appInstallationFetch.value?.id)
      dispatchAppInstallationGet(id, installationId, t);
  }, [t, id, installationId, dispatchAppInstallationGet, appInstallationFetch.value]);

  const panes = useMemo(
    () =>
      [
        {
          menuItem: t('details'),
          render: () => (
            <Tab.Pane>
              <AppInfo appFetch={appFetch} />
            </Tab.Pane>
          ),
        },
        {
          menuItem: t('installations'),
          render: () => (
            <Tab.Pane>
              <AppInstallations appFetch={appFetch} />
            </Tab.Pane>
          ),
        },
        hasRight(rights, 'apps/communities/read')
          ? {
              menuItem: t('communities'),
              render: () => (
                <Tab.Pane>
                  <AppCommunities />
                </Tab.Pane>
              ),
            }
          : undefined,
        hasRight(rights, 'apps/organisations/read')
          ? {
              menuItem: t('organisations'),
              render: () => (
                <Tab.Pane>
                  <AppOrganisations />
                </Tab.Pane>
              ),
            }
          : undefined,
        {
          menuItem: (
            <Menu.Menu position="right" key="menu">
              <HasRight right={UPDATE_APP_RIGHT}>
                {tab === TABS.DETAILS && id ? (
                  <Menu.Item
                    icon="pencil"
                    content={t('edit')}
                    onClick={() => navigate(`/apps/${id}/edit`)}
                  />
                ) : (
                  <div />
                )}
              </HasRight>
            </Menu.Menu>
          ),
        },
      ].filter(p => p),
    [appFetch, tab, t, id, rights, navigate]
  );

  if (
    installationId &&
    appInstallationFetch.fulfilled &&
    appInstallationFetch.value?.id === installationId &&
    id === appFetch.value?.id
  ) {
    return (
      <PageContent>
        <AppInstallationDetail
          appName={appName}
          appFetch={appFetch}
          appInstallationFetch={appInstallationFetch}
        />
      </PageContent>
    );
  }

  return (
    <>
      <PageTitle>
        <StyledBreadcrumb size="tiny">
          <BreadcrumbSection>
            <Link to="/apps">{t('apps_plural')}</Link>
          </BreadcrumbSection>
          <BreadcrumbDivider />
          <BreadcrumbSection>
            <span>{appName}</span>
          </BreadcrumbSection>
        </StyledBreadcrumb>
      </PageTitle>
      <PageContent>
        {appFetch.pending ? (
          <Loader active={appFetch.pending} inline="centered" />
        ) : (
          <>
            {tab === ACTIONS.EDIT && (
              <HasRight right={UPDATE_APP_RIGHT}>
                <EditApp open={id && tab === ACTIONS.EDIT} appFetch={appFetch} />
              </HasRight>
            )}
            {appFetch.value?.id === id && (
              <Tab
                activeIndex={TAB_INDEX_MAPPING[tab]}
                onTabChange={(e, { activeIndex }) => {
                  const activeTab = findKey(TAB_INDEX_MAPPING, index => index === activeIndex);
                  if (activeTab) {
                    navigate(`/apps/${id}/${activeTab}`);
                  }
                }}
                panes={panes}
              />
            )}
          </>
        )}
      </PageContent>
    </>
  );
};

AppDetail.propTypes = {
  dispatchAppGet: PropTypes.func.isRequired,
  appFetch: PropTypes.object.isRequired,
  dispatchAppInstallationGet: PropTypes.func.isRequired,
  appInstallationFetch: PropTypes.object.isRequired,
};

export default compose(appService, appInstallationService)(AppDetail);
